import CustomerService from "../CustomerService";
import {getEmptyCustomer, getEmptyBankAccount, getEmptyIdentification} from './helpers';
import _ from "lodash";
import CustomerLinkService from "../view/dashboard/customer-links/CustomerLinkService";
import AddressService from "../view/addresses/AddressService";
import {SOURCE_ADDRESS_LIST, SOURCE_CUSTOMER_BALANCE_PAGE, SOURCE_CUSTOMER_DASHBOARD} from "../../constants";
import RelationsService from "@/modules/relations/RelationsService";
import {hasPermission} from "@/core/directives/permission";

const getDefaultDashboardData = () => {
  return {
    addressesLoader: false,
    latestNotesLoader: false,
    customerLinksLoader: null,
    shippingAddress: null,
    billingAddress: null,
    customerLinks: [],
    latestNotes: [],
    hasExtraNotes: false,
    customerInterests: {
      buyer: [],
      vendor: [],
      loader: false
    },
    financialActivity: {
      buyer: {labels: [], series: []},
      vendor: {labels: [], series: []},
      loader: false
    },
    generalInfo: {
      data: {
        invoice_amount: {},
        paid_invoice_amount: {},
        outstanding_invoice_amount: {},
        invoices: 0,
        settlement_amount: {},
        paid_settlement_amount: {},
        outstanding_settlement_amount: {},
        settlements: 0,
      },
      loader: false
    },
    relations: {
      loader: false,
      notary: null,
      intermediary: null,
      contactPerson: null,
    }
  }
};

const getDefaultRelationModalData = () => {
  return {
    object: {},
    show: false,
    relationType: false,
    source: null,
    title: null
  }
}

export default {
  namespaced: true,
  state: {
    loading: false,
    customer: getEmptyCustomer(),
    dashboardData: getDefaultDashboardData(),
    addressListData: {
      loading: false,
      billingAddresses: [],
      shippingAddresses: []
    },
    termsData: {
      showTaxStatusModal: false,
      showPaymentMethodModal: false,
      showTaxRateModal: false,
      showVatNumberModal: false,
      showPaymentTermsModal: false,
      showSettlementDaysModal: false,
      showCreditLimitModal: false,
      showTrustLevelModal: false,
      showBankAccountModal: false,
      showCommissionsModal: false,
      showPremiumsModal: false,
      showRedFlagModal: false,
      tax_status_uuid: null,
      tax_rate: null,
      vat_number: null,
      payment_method: null,
      payment_terms: null,
      settlement_days: null,
      credit_limit: null,
      lot_handling_charge: null,
      minimum_commission: null,
      minimum_premium: null,
      identity_verification: null,
      identity_verification_rejected_reason: null,
      red_flagged: false,
      red_flag_comment: '',
      insurance_rate: null,
      rebate_rate: null,
      exclude_from_auto_payments: null,
      commissions: [],
      premiums: [],
      bank_account: getEmptyBankAccount(),
      identification: getEmptyIdentification(),
      identificationModal: {
        show: false,
        object: null
      },
      loading: false,
      kyc_status_approved: false,
      pre_approve_commission_bids: false,
      pre_approve_telephone_bids: false,
      showDynamicFieldModal: false,
      dynamicFieldName: null,
      is_pickup: false,
      kyc_status_verification: null,
      kyc_rejected_reason: null,
    },
    onlinePaymentMethods: {
      loading: false,
      data: []
    },
    customerLinkModal: {
      object: {},
      show: false,
      source: null
    },
    relationModal: getDefaultRelationModalData(),
    addressModal: {
      object: {},
      source: null,
      show: false,
    },
    balanceData: {
      reservationModal: {
        object: {},
        source: null,
        show: false,
      },
      reservations: {
        data: [],
        loading: false
      }
    },
    templatesToEmailsModal: {
      show: false,
      items: []
    },
    templatesToEmailsTableData: []
  },
  actions: {
    async updateViewCustomer({commit, dispatch, state}, data) {
      await commit('updateCustomer', {...state.customer, ...data});
      dispatch('initCustomer');
      if (hasPermission('view_customeraddress')) {
        dispatch('getDashboardAddresses');
      }
    },
    async getCustomer({commit, dispatch}, payload) {
      commit('toggleViewPageLoader', true);
      let response = await CustomerService.getCustomer(payload, 'deposits.currency,logo,accounting_code,trust_level,term' + (hasPermission('view_customeraddress') ? ',addresses' : ''));
      commit('toggleViewPageLoader', false);

      if (response.success) {
        await commit('updateCustomer', response.body);
        dispatch('initCustomer');
      }

      return response
    },
    async updateCustomer({commit}, customer) {
      let response = await CustomerService.updateCustomer(customer.uuid, customer, 'term');
      if (response.success) {
        await commit('updateCustomer', response.body);
      }
      return response;
    },
    async partialUpdateCustomer({commit}, {payload, expand=''}) {
      let response = await CustomerService.partialUpdateCustomer(payload, expand);
      if (response.success) {
        await commit('updateCustomer', response.body);
      }
      return response;
    },
    async initCustomer({commit, state}) {

      let customer = state.customer;

      if (customer.phone) {
        let phones = customer.phone.split(',');
        customer.phone1 = phones[0];
        customer.phone2 = phones[1];
      } else {
        customer.phone1 = '';
        customer.phone2 = '';
      }
      commit('updateCustomer', customer);
    },
    async getDashboardAddresses({commit, state}) {
      commit('toggleDashboardAddressesLoader', true);
      let response = await CustomerService.getCustomerAddresses(state.customer.uuid);
      commit('toggleDashboardAddressesLoader', false);

      if (response.success) {
        commit('updateDashboardAddresses', {
          billingAddress: response.body.find(address => address.type === 'billing' && address.is_primary === true),
          shippingAddress: response.body.find(address => address.type === 'shipping' && address.is_primary === true)
        });
      }

      return response;
    },
    async getDashboardRelations({state}) {
      state.dashboardData.relations.loader = true
      let response = await RelationsService.getRelations({
        object_uuid: state.customer.uuid,
        expand: "related_customer"
      });
      state.dashboardData.relations.loader = false

      if (response.success) {
        state.dashboardData.relations.intermediary = response.body.results.find(r => r.type === 'intermediary') || null;
        state.dashboardData.relations.notary = response.body.results.find(r => r.type === 'notary') || null;
        state.dashboardData.relations.contactPerson = response.body.results.find(r => r.type === 'contact_person') || null;
      }

      return response;
    },
    async getDashboardLatestNotes({commit, state}) {
      commit('toggleDashboardLatestNotesLoader', true);
      let response = await CustomerService.getCustomerNotes(state.customer.uuid);
      commit('toggleDashboardLatestNotesLoader', false);

      if (response.success && response.body.length > 0) {
        commit('updateDashboardLatestNotes', response.body)
      }

      return response.body;
    },
    async getDashboardCustomerLinks({commit, state}) {
      commit('toggleDashboardCustomerLinksLoader', true);
      let response = await CustomerService.getCustomerLinks(state.customer.uuid);
      commit('toggleDashboardCustomerLinksLoader', false);

      if (response.success && response.body.length > 0) {
        commit('updateDashboardCustomerLinks', response.body)
      }

      return response.body;
    },
    async getDashboardGeneralInfo({state}, payload) {
      state.dashboardData.generalInfo.loader = true;
      let response = await CustomerService.getCustomerGeneralInfo(payload);
      if (response.success) {
        state.dashboardData.generalInfo.data = {
          ...state.dashboardData.generalInfo.data,
          ...response.body
        };
      }
      state.dashboardData.generalInfo.loader = false
    },
    async getDashboardCustomerInterests({state}, payload) {
      state.dashboardData.customerInterests.loader = true;
      let response = await CustomerService.getCustomerInterests(payload);
      state.dashboardData.customerInterests.buyer = response.buyer;
      state.dashboardData.customerInterests.vendor = response.vendor;
      state.dashboardData.customerInterests.loader = false;
    },
    async getDashboardCustomerFinancialActivity({state}, payload) {
      state.dashboardData.financialActivity.loader = true;
      let response = await CustomerService.getCustomerFinancialActivity(payload);
      state.dashboardData.financialActivity.buyer = response.buyer;
      state.dashboardData.financialActivity.vendor = response.vendor;
      state.dashboardData.financialActivity.loader = false;
    },

    async getIdentificationData({commit, state}) {
      commit('toggleTermsLoader', true);
      let response = await CustomerService.getCustomerIdentification(state.customer.uuid);
      commit('toggleTermsLoader', false);

      if (response.success) {
        state.termsData.identification.dynamic_fields = response.body.dynamic_fields;
        const files = response.body.customer_identification_files.filter(f => f.attachment.uploaded)
        state.termsData.identification.government_issued_id_files = files.filter(file => file.type === 'government_issued_id').map(file => file.attachment)
        state.termsData.identification.proof_of_address_files = files.filter(file => file.type === 'proof_of_address').map(file => file.attachment)
        state.termsData.identification.employment_information_files = files.filter(file => file.type === 'employment_information').map(file => file.attachment)
        state.termsData.identification.company_id_documents_files = files.filter(file => file.type === 'company_id_documents').map(file => file.attachment)
      }
    },

    async getTermsData({commit, state}) {
      commit('toggleTermsLoader', true);
      let response = await CustomerService.getCustomer(state.customer.uuid, 'bank_account,term,identification.customer_identification_files.attachment.created_by');
      commit('toggleTermsLoader', false);

      if (response.success) {
        const identification = response.body.identification
        if (identification) {
          identification.customer_identification_files = identification.customer_identification_files.filter(file => file.attachment.uploaded)
          identification.government_issued_id_files = identification.customer_identification_files.filter(file => file.type === 'government_issued_id').map(file => file.attachment)
          identification.proof_of_address_files = identification.customer_identification_files.filter(file => file.type === 'proof_of_address').map(file => file.attachment)
          identification.employment_information_files = identification.customer_identification_files.filter(file => file.type === 'employment_information').map(file => file.attachment)
          identification.company_id_documents_files = identification.customer_identification_files.filter(file => file.type === 'company_id_documents').map(file => file.attachment)
          delete identification.customer_identification_files
        }
        await commit('updateTermsData', {
          tax_status_uuid: response.body.term.tax_status_uuid,
          bank_account: response.body.bank_account,
          commissions: response.body.term.commissions,
          premiums: response.body.term.premiums,
          vat_number: response.body.term.vat_number,
          tax_rate: response.body.term.tax_rate,
          payment_terms: response.body.term.payment_terms,
          settlement_days: response.body.term.settlement_days,
          credit_limit: response.body.term.credit_limit,
          lot_handling_charge: response.body.term.lot_handling_charge,
          minimum_commission: response.body.term.minimum_commission,
          minimum_premium: response.body.term.minimum_premium,
          insurance_rate: response.body.term.insurance_rate,
          rebate_rate: response.body.term.rebate_rate,
          identity_verification: response.body.term.identity_verification,
          identity_verification_rejected_reason: response.body.term.identity_verification_rejected_reason,
          red_flagged: response.body.term.red_flagged,
          payment_method: response.body.term.payment_method,
          red_flag_comment: response.body.term.red_flag_comment,
          kyc_status_verification: response.body.term.kyc_status_verification,
          kyc_rejected_reason: response.body.term.kyc_rejected_reason,
          is_pickup: response.body.term.is_pickup,
          identification: identification,
          pre_approve_commission_bids: response.body.term.pre_approve_commission_bids,
          pre_approve_telephone_bids: response.body.term.pre_approve_telephone_bids,
          exclude_from_auto_payments: response.body.term.exclude_from_auto_payments,
        });
      }

      return response
    },
    async getAddressListAddresses({commit, state}) {
      commit('toggleAddressListLoader', true);
      let response = await CustomerService.getCustomerAddresses(state.customer.uuid);
      commit('toggleAddressListLoader', false);

      if (response.success) {
        commit('updateAddressListAddresses', {
          billingAddresses: response.body.filter(address => address.type === 'billing'),
          shippingAddresses: response.body.filter(address => address.type === 'shipping')
        });
      }

      return response;
    },
    showCustomerLinkModal({commit}, {object = null, source} = {}) {
      commit('showCustomerLinkModal', {object: _.cloneDeep(object), source});
    },
    hideCustomerLinkModal({commit}) {
      commit('hideCustomerLinkModal');
    },
    async createCustomerLink({commit, state}, link) {
      let response = await CustomerLinkService.createLink(link);
      if (response.success && state.customerLinkModal.source === SOURCE_CUSTOMER_DASHBOARD) {
        commit('addDashboardCustomerLink', response.body);
      }
      return response;
    },
    async updateCustomerLink({commit, state}, link) {
      let response = await CustomerLinkService.updateLink(link.uuid, link);
      if (response.success && state.customerLinkModal.source === SOURCE_CUSTOMER_DASHBOARD) {
        commit('updateDashboardCustomerLink', response.body);
      }
      return response;
    },
    async removeCustomerLink({commit, state}, {source, uuid}) {
      let response = await CustomerLinkService.removeLink(uuid);

      if (response.success && source === SOURCE_CUSTOMER_DASHBOARD) {
        commit('updateDashboardCustomerLinks', state.dashboardData.customerLinks.filter(link => link.uuid !== uuid));
      }

      return response;
    },
    showAddressModal({commit}, {object = null, source} = {}) {
      commit('showAddressModal', {object: _.cloneDeep(object), source});
    },
    hideAddressModal({commit}) {
      commit('hideAddressModal');
    },
    async createAddress({commit, state, dispatch}, address) {
      let response = await AddressService.createAddress(address);
      if (response.success) {
        if (state.addressModal.source === SOURCE_ADDRESS_LIST) {
          if (address.is_primary) {
            await dispatch('removeAllPrimaries', response.body.type);
          }
          commit('addAddressListAddress', response.body);
        } else if (state.addressModal.source === SOURCE_CUSTOMER_DASHBOARD) {
          dispatch('getDashboardAddresses');
        }
      }
      return response;
    },
    async updateAddress({commit, state, dispatch}, address) {
      let response = await AddressService.updateAddress(address.uuid, address);
      if (response.success) {
        if (state.addressModal.source === SOURCE_ADDRESS_LIST) {
          if (address.is_primary) {
            await dispatch('removeAllPrimaries', response.body.type);
          }
          commit('updateAddressListAddress', response.body);
        } else if (state.addressModal.source === SOURCE_CUSTOMER_DASHBOARD) {
          dispatch('getDashboardAddresses');
        }
      }
      return response;
    },
    async removeAllPrimaries({commit, state}, type) {
      let billingAddresses = state.addressListData.billingAddresses;
      let shippingAddresses = state.addressListData.shippingAddresses;

      if (type === 'billing') {
        billingAddresses = billingAddresses.map(address => {
          address.is_primary = false;
          return address;
        });
      } else {
        shippingAddresses = shippingAddresses.map(address => {
          address.is_primary = false;
          return address;
        });
      }

      commit('updateAddressListAddresses', {billingAddresses, shippingAddresses});
      return true;
    },
    async removeAddress({commit, state}, {source, uuid, type}) {
      let response = await AddressService.removeAddress(uuid);

      if (response.success && source === SOURCE_ADDRESS_LIST) {
        let billingAddresses = state.addressListData.billingAddresses;
        let shippingAddresses = state.addressListData.shippingAddresses;

        if (type === 'billing') {
          billingAddresses = billingAddresses.filter(address => address.uuid !== uuid);
        } else {
          shippingAddresses = shippingAddresses.filter(address => address.uuid !== uuid);
        }

        commit('updateAddressListAddresses', {billingAddresses, shippingAddresses})
      }

      return response;
    },
    async toggleVatNumberModal({commit}, payload) {
      commit('toggleVatNumberModal', payload);
    },
    async togglePaymentMethodModal({commit}, payload) {
      commit('togglePaymentMethodModal', payload);
    },
    async togglePaymentTermsModal({commit}, payload) {
      commit('togglePaymentTermsModal', payload);
    },
    async toggleSettlementDaysModal({commit}, payload) {
      commit('toggleSettlementDaysModal', payload);
    },
    async toggleCreditLimitModal({commit}, payload) {
      commit('toggleCreditLimitModal', payload);
    },
    async toggleBankAccountModal({commit}, payload) {
      commit('toggleBankAccountModal', payload);
    },
    async toggleCommissionsModal({commit}, payload) {
      commit('toggleCommissionsModal', payload);
    },
    async togglePremiumsModal({commit}, payload) {
      commit('togglePremiumsModal', payload);
    },
    async toggleTaxStatusModal({commit}, payload) {
      commit('toggleTaxStatusModal', payload);
    },
    async toggleRedFlagModal({commit}, payload) {
      commit('toggleRedFlagModal', payload);
    },
    async toggleTaxRateModal({commit}, payload) {
      commit('toggleTaxRateModal', payload);
    },
    async showTemplatesToEmailsModal({commit}, {items = [], customer = null} = {}) {
      commit('showTemplatesToEmailsModal', {items: _.cloneDeep(items), customer: _.cloneDeep(customer)});
    },
    async hideTemplatesToEmailsModal({commit}) {
      commit('hideTemplatesToEmailsModal');
    },
    async setTemplatesToEmailsTableData({commit}, items) {
      commit('setTemplatesToEmailsTableData', items);
    },
    async toggleTrustLevelModal({commit}, payload) {
      commit('toggleTrustLevelModal', payload);
    },
    async toggleDynamicFieldModal({commit}, {show, name}) {
      commit('toggleDynamicFieldModal', {show, name});
    },
    async createCustomerTrustLevel({commit}, trustLevel) {
      let response = await CustomerService.createTrustLevel(trustLevel);
      if (response.success) {
        commit('setCustomerTrustLevel', response.body);
      }
      return response;
    },
    async updateCustomerTrustLevel({commit}, trustLevel) {
      let response = await CustomerService.updateTrustLevel(trustLevel.uuid, trustLevel);
      if (response.success) {
        commit('setCustomerTrustLevel', response.body);
      }
      return response;
    },
    async updateCustomerEmailsToTemplates({state, commit}, items) {
      let data = []
      items.forEach(item => {
        item.emails.forEach(email =>
          data.push({
            customer_uuid: state.customer.uuid,
            email: email,
            key: item.template_type,
            uuid: item.uuid
          })
        )
      });
      let response = await CustomerService.updateCustomerEmailsToTemplates({id: state.customer.uuid, data: data});
      if (response.success) {
        items.forEach((item, index) => items[index] = {template_type: item.template_type, emails: item.emails});
        commit('setTemplatesToEmailsTableData', items);
      }
      return response
    },
    async saveTermsPartialData({commit, state}, payload) {
      let response = await CustomerService.partialUpdateCustomerTerms({id: state.customer.uuid, data: payload});

      if (response.success) {
        commit('updateTermsData', payload);
      }

      return response;
    },
    async saveCustomerIdentification({commit, state}, payload) {
      let response = await CustomerService.updateCustomerIdentification({id: state.customer.uuid, data: payload});

      if (response.success) {
        const files = response.body.customer_identification_files.filter(f => f.attachment.uploaded)
        commit('updateTermsData', {
          identification: {
            personal_identification_number: response.body.personal_identification_number,
            document_type: response.body.document_type,
            document_number: response.body.document_number,
            document_issue_date: response.body.document_issue_date,
            document_expiry_date: response.body.document_expiry_date,
            date_of_birth: response.body.date_of_birth,
            citizenship_code: response.body.citizenship_code,
            residency_code: response.body.residency_code,
            country_of_birth_code: response.body.country_of_birth_code,
            place_of_issue: response.body.place_of_issue,
            company_reg_number: response.body.company_reg_number,
            government_issued_id_files: files.filter(file => file.type === 'government_issued_id').map(file => file.attachment),
            proof_of_address_files: files.filter(file => file.type === 'proof_of_address').map(file => file.attachment),
            employment_information_files: files.filter(file => file.type === 'employment_information').map(file => file.attachment),
            company_id_documents_files: files.filter(file => file.type === 'company_id_documents').map(file => file.attachment),
          }
        });
      }

      return response;
    },
    async saveTermsBankAccount({commit}, payload) {
      let response = await CustomerService.createOrUpdateBankAccount(payload);

      if (response.success) {
        commit('updateTermsData', {bank_account: response.body});
      }

      return response;
    },
    async removeBankAccount({commit, state}) {
      let response = await CustomerService.removeBankAccount(state.termsData.bank_account.customer_uuid);

      if (response.success) {
        commit('updateTermsData', {bank_account: null});
      }

      return response;
    },

    async toggleCreditCardVerification({commit, state}, checked) {
      const payload = {'credit_card_verified_at': checked ? new Date() : null};
      let response = await CustomerService.partialUpdateCustomer({id: state.customer.uuid, data: payload});

      if (response.success) {
        commit('updateCreditCardVerification', payload);
      }

      return response;
    },
    async toggleExcludeFromAutoPayments({commit, state}, checked) {
      const payload = {'exclude_from_auto_payments': checked};
      let response = await CustomerService.partialUpdateCustomerTerms({id: state.customer.uuid, data: payload});

      if (response.success) {
        commit('updateTermsData', payload);
      }

      return response;
    },
    async updateCustomerTermsDynamicFields({state}, payload) {
      let response = await CustomerService.createOrUpdateTermsDynamicFields(state.customer.uuid, payload);
      return response;
    },

    resetDashboardData({state}) {
      state.dashboardData = getDefaultDashboardData();
    },

    async updateCustomerDeposits({state}) {
      let response = await CustomerService.getCustomer(state.customer.uuid, 'deposits.currency');
      if (response.success) {
        state.customer.deposits = response.body.deposits;
      }
    },
    async getCustomerReservations({state, commit}) {
      commit('toggleCustomerReservationsLoader', true);
      let response = await CustomerService.getCustomerReservations({
        customer_uuid: state.customer.uuid,
        expand: 'auction,currency'
      });
      if (response.success) {
        commit('updateCustomerReservationsData', response.body);
      }
      commit('toggleCustomerReservationsLoader', false);
    },
    async deleteCustomerReservation({state, dispatch}, uuid) {
      let response = await CustomerService.removeCustomerReservation(uuid);
      if (response.success) {
        dispatch('updateCustomerDeposits');
        state.balanceData.reservations.data = state.balanceData.reservations.data.filter(r => r.uuid !== uuid);
      }
      return response;
    },
    showCustomerReservationModal({commit}, {object = null, source} = {}) {
      commit('showCustomerReservationModal', {object: _.cloneDeep(object), source});
    },
    hideCustomerReservationModal({commit}) {
      commit('hideCustomerReservationModal');
    },
    async createCustomerReservation({commit, state, dispatch}, reservation) {
      let response = await CustomerService.createCustomerReservation(reservation);
      if (response.success && state.balanceData.reservationModal.source === SOURCE_CUSTOMER_BALANCE_PAGE) {
        dispatch('updateCustomerDeposits');
        commit('addReservationToBalanceReservationsData', response.body);
      }
      return response;
    },
    async updateCustomerReservation({commit, state, dispatch}, reservation) {
      let response = await CustomerService.updateCustomerReservation(reservation.uuid, reservation);
      if (response.success && state.balanceData.reservationModal.source === SOURCE_CUSTOMER_BALANCE_PAGE) {
        dispatch('updateCustomerDeposits');
        commit('updateCustomerReservationsDataReservation', response.body);
      }
      return response;
    },
    async createCustomerLogo(state, {formData, id, onUploadProgress}) {
      return await CustomerService.createCustomerLogo(formData, id, onUploadProgress);
    },
    async deleteCustomerLogo(state, payload) {
      return await CustomerService.deleteCustomerLogo(payload);
    },
    showCustomerIdentificationModal({commit}, {object, source = null}) {
      commit('showCustomerIdentificationModal', {object, source})
    },
    hideCustomerIdentificationModal({commit}) {
      commit('hideCustomerIdentificationModal')
    },
    showRelationModal({commit}, {object, source = null, relationType, title = null}) {
      commit('showRelationModal', {object, source, relationType, title})
    },
    hideRelationModal({commit}) {
      commit('hideRelationModal')
    },
    async getOnlinePaymentMethods({commit, state}) {
      commit('toggleOnlinePaymentMethodsLoader', true);
      let response = await CustomerService.getOnlinePaymentMethods({customer_uuid: state.customer.uuid});
      commit('toggleOnlinePaymentMethodsLoader', false);
      if (response.success) {
        await commit('updateOnlinePaymentMethods', response.body);
      }

      return response
    },
  },
  mutations: {
    updateCustomer: (state, customer) => {
      state.customer = {...state.customer, ...customer}
      state.termsData = {...state.termsData, ...state.customer.term}
    },
    toggleTaxStatusModal: (state, data) => state.termsData.showTaxStatusModal = data,
    toggleTaxRateModal: (state, data) => state.termsData.showTaxRateModal = data,
    toggleVatNumberModal: (state, data) => state.termsData.showVatNumberModal = data,
    togglePaymentTermsModal: (state, data) => state.termsData.showPaymentTermsModal = data,
    togglePaymentMethodModal: (state, data) => state.termsData.showPaymentMethodModal = data,
    toggleSettlementDaysModal: (state, data) => {
      state.termsData.showSettlementDaysModal = data
    },
    toggleCreditLimitModal: (state, data) => state.termsData.showCreditLimitModal = data,
    toggleBankAccountModal: (state, data) => state.termsData.showBankAccountModal = data,
    toggleCommissionsModal: (state, data) => state.termsData.showCommissionsModal = data,
    togglePremiumsModal: (state, data) => state.termsData.showPremiumsModal = data,
    toggleRedFlagModal: (state, data) => state.termsData.showRedFlagModal = data,
    toggleTrustLevelModal: (state, data) => state.termsData.showTrustLevelModal = data,
    toggleDynamicFieldModal: (state, {show, name}) => {
      state.termsData.showDynamicFieldModal = show;
      state.termsData.dynamicFieldName = name;
    },
    toggleViewPageLoader: (state, data) => state.loading = data,
    toggleDashboardAddressesLoader: (state, data) => state.dashboardData.addressesLoader = data,
    updateDashboardAddresses: (state, {billingAddress, shippingAddress}) => {
      state.dashboardData.billingAddress = billingAddress;
      state.dashboardData.shippingAddress = shippingAddress;
    },
    toggleDashboardLatestNotesLoader: (state, data) => state.dashboardData.latestNotesLoader = data,
    toggleDashboardCustomerLinksLoader: (state, data) => state.dashboardData.customerLinksLoader = data,
    toggleTermsLoader: (state, data) => state.termsData.loading = data,
    toggleOnlinePaymentMethodsLoader: (state, data) => state.onlinePaymentMethods.loading = data,
    showTemplatesToEmailsModal: (state, {items}) => {
      state.templatesToEmailsModal.show = true;
      state.templatesToEmailsModal.items = items;
    },
    hideTemplatesToEmailsModal: (state) => {
      state.templatesToEmailsModal.show = false;
    },
    setTemplatesToEmailsTableData: (state, items) => {
      state.templatesToEmailsTableData = items;
    },
    updateDashboardLatestNotes: (state, latestNotes) => {
      state.dashboardData.hasExtraNotes = latestNotes.length > 3;
      state.dashboardData.latestNotes = [];
      for (let i = 0; i < latestNotes.length; i++) {
        state.dashboardData.latestNotes.push(latestNotes[i]);
        if (i === 2) {
          break;
        }
      }
    },
    updateDashboardCustomerLinks: (state, links) => state.dashboardData.customerLinks = links,
    addDashboardCustomerLink: (state, customerLink) => state.dashboardData.customerLinks.push(customerLink),
    updateDashboardCustomerLink: (state, customerLink) => {
      const index = state.dashboardData.customerLinks.findIndex(link => link.uuid === customerLink.uuid);
      state.dashboardData.customerLinks[index] = {...state.dashboardData.customerLinks[index], ...customerLink};
      state.dashboardData.customerLinks = [...state.dashboardData.customerLinks];
    },
    updateTermsData: (state, data) => {
      state.termsData = {...state.termsData, ...data};
      if (state.customer && state.customer.terms) {
        state.customer.terms = {...state.customer.terms, ...state.termsData}
      }
    },
    updateOnlinePaymentMethods: (state, data) => {
      state.onlinePaymentMethods.data = [...data.results];
    },
    updateCreditCardVerification: (state, data) => {
      state.customer = {...state.customer, ...data};
    },
    showCustomerLinkModal: (state, {object, source}) => {
      state.customerLinkModal.show = true;
      state.customerLinkModal.object = object;
      state.customerLinkModal.source = source;
    },
    hideCustomerLinkModal: state => state.customerLinkModal.show = false,
    showAddressModal: (state, {object, source}) => {
      state.addressModal.show = true;
      state.addressModal.object = object;
      state.addressModal.source = source;
    },
    hideAddressModal: state => state.addressModal.show = false,
    toggleAddressListLoader: (state, data) => state.addressListData.loading = data,
    updateAddressListAddresses: (state, {billingAddresses, shippingAddresses}) => {
      state.addressListData.billingAddresses = billingAddresses;
      state.addressListData.shippingAddresses = shippingAddresses;
    },
    addAddressListAddress: (state, address) => {
      if (address.type === 'billing') {
        state.addressListData.billingAddresses.push(address);
      } else {
        state.addressListData.shippingAddresses.push(address);
      }
    },
    updateAddressListAddress: (state, address) => {
      if (address.type === 'shipping') {
        const index = state.addressListData.shippingAddresses.findIndex(add => add.uuid === address.uuid);
        state.addressListData.shippingAddresses[index] = {...state.addressListData.shippingAddresses[index], ...address};
        state.addressListData.shippingAddresses = [...state.addressListData.shippingAddresses];
      } else {
        const index = state.addressListData.billingAddresses.findIndex(add => add.uuid === address.uuid);
        state.addressListData.billingAddresses[index] = {...state.addressListData.billingAddresses[index], ...address};
        state.addressListData.billingAddresses = [...state.addressListData.billingAddresses];
      }
    },
    updateCustomerReservationsData: (state, data) => state.balanceData.reservations.data = data,
    addReservationToBalanceReservationsData: (state, data) => state.balanceData.reservations.data.push(data),
    updateCustomerReservationsDataReservation: (state, reservation) => {
      const index = state.balanceData.reservations.data.findIndex(r => r.uuid === reservation.uuid);
      state.balanceData.reservations.data[index] = {...state.balanceData.reservations.data[index], ...reservation};
      state.balanceData.reservations.data = [...state.balanceData.reservations.data];
    },
    toggleCustomerReservationsLoader: (state, data) => state.balanceData.reservations.loading = data,
    showCustomerReservationModal: (state, {object, source}) => {
      state.balanceData.reservationModal.show = true;
      state.balanceData.reservationModal.object = object;
      state.balanceData.reservationModal.source = source;
    },
    hideCustomerReservationModal: state => state.balanceData.reservationModal.show = false,
    showCustomerIdentificationModal: (state, {object}) => {
      state.termsData.identificationModal.show = true
      state.termsData.identificationModal.object = object
    },
    hideCustomerIdentificationModal: (state) => {
      state.termsData.identificationModal.show = false
      state.termsData.identificationModal.object = null
    },
    showRelationModal: (state, {object, relationType, source, title}) => {
      state.relationModal.show = true
      state.relationModal.object = object
      state.relationModal.relationType = relationType
      state.relationModal.source = source
      state.relationModal.title = title
    },
    hideRelationModal: (state) => {
      state.relationModal = getDefaultRelationModalData()
    },
    setCustomerTrustLevel: (state, trustLevel) => state.customer.trust_level = {...trustLevel}
  }
}
